import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { Bell, MicOff } from "lucide-react";
import { Popover, PopoverContent, PopoverTrigger } from "./ui/popover";
import { api } from "../lib/api";
import { useAuth } from "../context/AuthContext";

function seenKey(userId) {
  return `exopilot_notifications_seen_${userId}`;
}

function loadSeenIds(userId) {
  try {
    return new Set(JSON.parse(localStorage.getItem(seenKey(userId))) || []);
  } catch {
    return new Set();
  }
}

export default function NotificationBell() {
  const { user } = useAuth();
  const [notifications, setNotifications] = useState([]);
  const [inactivityDays, setInactivityDays] = useState(3);
  const [seenIds, setSeenIds] = useState(() => new Set());
  const [open, setOpen] = useState(false);

  useEffect(() => {
    if (!user?.id) return;
    setSeenIds(loadSeenIds(user.id));
    api.get("/notifications").then(({ data }) => {
      setNotifications(data.notifications);
      setInactivityDays(data.inactivity_days);
    }).catch(() => {});
  }, [user?.id]);

  const unseen = notifications.filter((n) => !seenIds.has(n.employee_id));
  const count = notifications.length;
  const showBadge = unseen.length > 0;

  const handleOpenChange = (next) => {
    setOpen(next);
    if (next && user?.id) {
      const merged = new Set(seenIds);
      notifications.forEach((n) => merged.add(n.employee_id));
      setSeenIds(merged);
      localStorage.setItem(seenKey(user.id), JSON.stringify([...merged]));
    }
  };

  return (
    <Popover open={open} onOpenChange={handleOpenChange}>
      <PopoverTrigger asChild>
        <button data-testid="notification-bell" className="relative p-2 rounded-md text-neutral-500 hover:bg-neutral-100 hover:text-[#111] transition-colors">
          <Bell className="w-5 h-5" />
          {showBadge && (
            <span data-testid="notification-count" className="absolute top-0.5 right-0.5 min-w-[15px] h-[15px] px-[3px] rounded-full bg-red-600 text-white text-[9px] font-medium flex items-center justify-center leading-none">
              {unseen.length > 9 ? "9+" : unseen.length}
            </span>
          )}
        </button>
      </PopoverTrigger>
      <PopoverContent data-testid="notification-panel" align="end" sideOffset={10}
        className="w-80 bg-white border border-neutral-200 rounded-lg shadow-lg p-0 overflow-hidden">
        <div className="px-4 py-3 border-b border-neutral-100">
          <p className="text-sm font-medium">Inactivity alerts</p>
          <p className="text-xs text-neutral-400 mt-0.5">Employees who haven't uploaded audio in {inactivityDays}+ days</p>
        </div>
        {count === 0 ? (
          <p className="px-4 py-6 text-sm text-neutral-400 text-center">Everyone's reporting in. No inactivity to flag.</p>
        ) : (
          <ul className="max-h-80 overflow-y-auto divide-y divide-neutral-100">
            {notifications.map((n) => (
              <li key={n.employee_id}>
                <Link to={`/console/employees/${n.employee_id}`} onClick={() => setOpen(false)}
                  data-testid={`notification-item-${n.employee_id}`}
                  className="flex items-start gap-3 px-4 py-3 hover:bg-neutral-50 transition-colors">
                  <span className="w-8 h-8 rounded-full bg-red-50 border border-red-200 flex items-center justify-center shrink-0 mt-0.5">
                    <MicOff className="w-3.5 h-3.5 text-red-600" />
                  </span>
                  <div className="min-w-0">
                    <p className="text-sm font-medium truncate">{n.employee_name}</p>
                    <p className="text-xs text-neutral-500">{n.position ? `${n.position} · ` : ""}{n.department}</p>
                    <p className="text-[11px] text-red-600 font-mono-data mt-1">
                      {n.last_session_date ? `Last upload ${n.last_session_date} · ${n.days_inactive}d ago` : `No uploads since joining · ${n.days_inactive}d`}
                    </p>
                  </div>
                </Link>
              </li>
            ))}
          </ul>
        )}
      </PopoverContent>
    </Popover>
  );
}
